import React, { useEffect, useState } from 'react';
import FeatherIcon from './FeatherIcon';
import { apiBase } from '../lib/api';

interface Impact {
  readonly animalsHelped: number;
  readonly vouchersIssued: number;
  readonly fundsRaised: number;
  readonly partnerOrgs: number;
  readonly currency: string;
}

const API_BASE = apiBase;
const usd = (n: number): string => `$${Math.round(n).toLocaleString('en-US')}`;

const ProjectOne: React.FC = () => {
  const [impact, setImpact] = useState<Impact | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${API_BASE}/api/impact`, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data: Impact) => setImpact(data))
      .catch((e) => { if (e.name !== 'AbortError') setFailed(true); });
    return () => controller.abort();
  }, []);

  const animalsHelped = impact?.animalsHelped ?? 0;
  const fundsRaised = impact?.fundsRaised ?? 0;
  const partnerOrgs = impact?.partnerOrgs ?? 0;

  return (
    <section id="project" className="py-20">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-4 text-gray-800">Project One: Voucher Program</h2>
        </div>

        <div className="flex flex-col md:flex-row items-center mb-16">
          <div className="md:w-1/2 mb-10 md:mb-0">
            <h3 className="text-2xl font-bold mb-6 text-gray-800">Donation-funded essential veterinary services</h3>
            {/* Not max-w-prose: it is 65*ch*, and Poppins has a wide zero, so it resolved to
                735px here and barely constrained anything. Explicit cap instead. */}
            <p className="text-lg mb-6 text-gray-700 max-w-2xl">
              Our voucher program provides free access to critical veterinary services for pets in need. Each voucher currently covers end to end spay or neuter procedures.
            </p>
          </div>
          <div className="md:w-1/2 flex justify-center">
            <div className="card p-8 max-w-md w-full">
              <div className="flex items-center justify-between mb-6">
                <h4 className="text-xl font-bold text-center text-gray-800">Program Impact</h4>
                <span className="text-xs font-medium text-gray-400">
                  {impact ? 'Live' : failed ? 'Live data unavailable' : 'Loading…'}
                </span>
              </div>
              <div className="space-y-5">
                <div className="flex justify-between items-baseline">
                  <span className="text-gray-700">Animals Helped</span>
                  <span className="text-2xl font-bold text-gray-800 tabular-nums">{animalsHelped.toLocaleString('en-US')}</span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-gray-700">Funds Raised</span>
                  <span className="text-2xl font-bold text-gray-800 tabular-nums">{usd(fundsRaised)}</span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-gray-700">Partner Organizations</span>
                  <span className="text-2xl font-bold text-gray-800 tabular-nums">{partnerOrgs.toLocaleString('en-US')}</span>
                </div>
                <div className="flex items-center text-sm text-gray-500 pt-1">
                  <FeatherIcon name="map-pin" className="text-tertiary mr-1" />
                  Currently serving Los Angeles
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectOne;
